import React from 'react';
import I18n from 'i18n-js';

import CoreLink from '@reverbdotcom/commons/src/components/core_link';
import { RCToast } from '@reverbdotcom/cadence/components';
import { ToastCloseType } from '@reverbdotcom/cadence/components/types';
import { trackEvent } from '@reverbdotcom/commons/src/elog/mparticle_tracker';
import { buildClickedAddFavoriteSearch, buildClickedRemoveFavoriteSearch, buildClickedToastMessage } from '@reverbdotcom/commons/src/event_tracking/favorites';
import { useURL } from '@reverbdotcom/commons/src/url_context';
import { MutationResult } from '@reverbdotcom/commons/src/useMutation';

import { useFindFavorite, FavoriteModal } from './find_favorite_context';
import SaveFavoriteModal from './favorite_modal';
import { RemoveFavoriteModal } from './remove_favorite_modal';

import { Paths } from '../shared/url_helpers';
import { CoreFindFavorite, UpsertMyFavorite } from '../../schemas/rql';

export function FindFavoriteLayout() {
  const { favorite, setFavorite, displayedModal, setDisplayedModal, isToastOpen, setIsToastOpen } = useFindFavorite();
  const url = useURL();

  const updateFavorite = function (key: 'feedEnabled' | 'emailEnabled', value: boolean) {
    const newFavorite = { ...favorite, [key]: value };

    if (key === 'feedEnabled' && !value) {
      newFavorite.emailEnabled = false;
    }

    setFavorite(newFavorite);
  };

  const handleUpsert = function (result: MutationResult<UpsertMyFavorite.Mutation>) {
    const newFavorite: CoreFindFavorite.Favorite = {
      ...favorite,
      id: result.data.upsertMyFavorite?.favorite?.id,
      favorited: result.data.upsertMyFavorite?.favorite?.favorited,
    };
    trackEvent(buildClickedAddFavoriteSearch(newFavorite, url));
    setFavorite(newFavorite);
    setIsToastOpen(true);
  };

  const handleRemove = function () {
    const newFavorite: CoreFindFavorite.Favorite = {
      ...favorite,
      favorited: false,
    };
    trackEvent(buildClickedRemoveFavoriteSearch(newFavorite, url));
    setFavorite(newFavorite);
  };

  const handleToastClose = function (closeType: ToastCloseType) {
    setIsToastOpen(false);

    if (closeType === 'dismiss' || closeType === 'swipe') {
      trackEvent(buildClickedToastMessage(url));
    }
  };

  return (
    <>
      <SaveFavoriteModal
        isOpen={displayedModal === FavoriteModal.Save}
        handleClose={() => { setDisplayedModal(FavoriteModal.None); }}
        handleUpdate={updateFavorite}
        modalTitle={I18n.t('discovery.favorites.confirmModalTitle')}
        searchTitle={favorite.title}
        feedEnabled={favorite.feedEnabled}
        emailEnabled={favorite.emailEnabled}
        queryParams={favorite.queryParams}
        handleMutationResult={handleUpsert}
      />
      <RemoveFavoriteModal
        handleRemove={handleRemove}
      />
      {/*
        This renders separate RCToast components for the add and remove states,
        because the add version has a link prop and the remove version does not.
        Using 1 RCToast with a conditional link prop causes the toast to not render when the link is null.
      */}
      <RCToast
        key="favorite--add"
        theme="watch-add"
        isOpen={isToastOpen && favorite.favorited}
        onClose={handleToastClose}
        text={I18n.t('discovery.favorites.toast.added')}
        link={
          <CoreLink
            to={Paths.myFavoritesSearches.expand({})}
            clickEvent={buildClickedToastMessage(url)}
          >
            {I18n.t('cadence.RCToast.linkDefault')}
          </CoreLink>
        }
      />
      <RCToast
        key="favorite--remove"
        theme="watch-remove"
        isOpen={isToastOpen && !favorite.favorited}
        onClose={handleToastClose}
        text={I18n.t('discovery.favorites.toast.removed')}
      />
    </>
  );
}
